import { Page, pickSeoProps } from '@components/Page'
import CheckerboardItem from '@components/_rcpo/CheckerboardItem'
import ImageBanner from '@components/_rcpo/ImageBanner'
import { RCPOLandingPageHero } from '@components/_rcpo/LandingPageHero'
import { RCPOLatestWatches } from '@components/_rcpo/LatestWatches'
import { RCPOPage } from '@components/_rcpo/Page'
import KeepExploring from '@components/_rcpo/Sliders/KeepExploring'
import TextBlock from '@components/_rcpo/TextBlock'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { RcpoLandingPage } from '@lib/kontent/models/RcpoLandingPage'
import { RolexCheckerboardItem } from '@lib/kontent/models/RolexCheckerboardItem'
import { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'

type RCPOHomepageProps = SingleItemResponse<RcpoLandingPage>

const RCPOHomepage: NextPage<RCPOHomepageProps> = ({ item, modular_content }) => {
  const router = useRouter()
  const checkerboardItems = item.elements.checkerboard.value.map(
    (key) => modular_content[key].elements,
  ) as RolexCheckerboardItem[]
  const keepExploringItems = item.elements.items.value.map((key) => modular_content[key].elements)

  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateNavigation(router.locales, 'rcpo.index.url')}
      noindex={router.locale === 'de'}
    >
      <RCPOPage pageType="home page">
        <RCPOLandingPageHero
          mobileImage={item.elements.mobile_image}
          desktopImage={item.elements.desktop_image}
          title={item.elements.title.value}
          buttonText={item.elements.button_text.value}
        />
        <TextBlock
          headingTag="h2"
          preTitle="Rolex Certified Pre-Owned"
          title={item.elements.latest_watches_title.value}
          description={item.elements.latest_watches_description.value}
          className="pt-8 sm:pt-16"
        />
        <RCPOLatestWatches buttonTitle={item.elements.latest_watches_button_text.value} />
        <ImageBanner
          desktopImage={item.elements.first_banner_desktop_image}
          mobileImage={item.elements.first_banner_mobile_image}
        />
        {checkerboardItems.slice(0, 3).map((item, key) => (
          <CheckerboardItem
            key={key}
            desktopImage={item.desktop_image}
            mobileImage={item.mobile_image}
            title={item.title.value}
            description={item.description.value}
            variant={key % 2 ? 'text-image' : 'image-text'}
            buttonTitle={item.button_title.value}
            buttonUrl={item.button_url.value}
          />
        ))}
        <ImageBanner
          desktopImage={item.elements.second_banner_desktop_image}
          mobileImage={item.elements.second_banner_mobile_image}
        />
        <KeepExploring items={keepExploringItems} title={item.elements.title_7d9cc03.value} />
      </RCPOPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const lang = locale === 'de' ? 'en' : locale
  try {
    const content = await getPageByCodename<RcpoLandingPage>({
      codename: 'rcpo_landing_page',
      lang: lang as string,
      preview,
    })

    return {
      props: {
        ...content,
        ...(await getServerSideTranslations(lang as string)),
      },
    }
  } catch (error) {
    throw error
  }
}

export default RCPOHomepage
